import { useQuery } from "@tanstack/react-query";
import { slotService } from "@/services";
import useAuth from "@/hooks/stores/useAuth";

// Query keys for slots
export const slotKeys = {
  all: ['slots'],
  interviewer: ['slots', 'interviewer'],
  interviewerPaginated: (page, perPage, userId) => [...slotKeys.interviewer, 'paginated', page, perPage, userId],
  counts: ['slots', 'counts'],
  user: ['slots', 'user'],
  userPaginated: (page, perPage, userId) => [...slotKeys.user, 'paginated', page, perPage, userId],
  single: (id) => [...slotKeys.all, id],
  specific: (userId) => ['slots', 'specific', userId],
};

/**
 * Hook for fetching interviewer slots with pagination and caching
 * @param {number} page - Current page number (default: 1)
 * @param {number} perPage - Items per page (default: 10)
 * @param {object} options - Additional React Query options
 * @returns {object} Query result with slots data, pagination info, loading state, etc.
 * 
 * @example
 * const { data, isLoading, error } = useInterviewerSlots(1, 10);
 * const { slots, pagination } = data || {};
 */
export const useInterviewerSlots = (page = 1, perPage = 10, options = {}) => {
  const { userId, isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: slotKeys.interviewerPaginated(page, perPage, userId),
    queryFn: () => slotService.getAllSlotsForInterviewer(page, perPage),
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 10, // 10 minutes
    keepPreviousData: true, // Prevents loading states when changing pages
    enabled: isAuthenticated && !!userId,
    select: (data) => ({
      slots: data?.slots || [],
      pagination: {
        current_page: data?.current_page || 1,
        last_page: data?.last_page || 1,
        per_page: data?.per_page || perPage,
        total: data?.total || 0
      }
    }),
    ...options
  });
};

/**
 * Hook for fetching slot counts for dashboard cards
 * @param {object} options - Additional React Query options
 * @returns {object} Query result with slot counts data
 * 
 * @example
 * const { data, isLoading, error } = useSlotCounts();
 * const { total_slots, location_counts } = data || {};
 */
export const useSlotCounts = (options = {}) => {
  const { userId, isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: [...slotKeys.counts, userId],
    queryFn: () => slotService.getSlotCounts(),
    staleTime: 1000 * 60 * 2, // 2 minutes (fresher data for counts)
    cacheTime: 1000 * 60 * 5, // 5 minutes
    enabled: isAuthenticated && !!userId,
    select: (data) => ({
      total_slots: data?.total_slots || 0,
      location_counts: {
        home: data?.location_counts?.home || 0,
        studio: data?.location_counts?.studio || 0
      }
    }),
    ...options
  });
};

/**
 * Hook for fetching user slots with pagination (for future use)
 * @param {number} page - Current page number (default: 1)
 * @param {number} perPage - Items per page (default: 10)
 * @param {object} options - Additional React Query options
 * @returns {object} Query result with slots data, pagination info, loading state, etc.
 */
export const useUserSlots = (page = 1, perPage = 10, options = {}) => {
  const { userId, isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: slotKeys.userPaginated(page, perPage, userId),
    queryFn: () => slotService.getUserSlots(page, perPage),
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    keepPreviousData: true,
    enabled: isAuthenticated && !!userId,
    select: (data) => ({
      slots: data?.slots || [],
      pagination: {
        current_page: data?.current_page || 1,
        last_page: data?.last_page || 1,
        per_page: data?.per_page || perPage,
        total: data?.total || 0
      }
    }),
    ...options
  });
};

/**
 * Hook for fetching single slot details
 * @param {string|number} id - Slot ID
 * @param {object} options - Additional React Query options
 * @returns {object} Query result with slot data, loading state, etc.
 * 
 * @example
 * const { data: slot, isLoading } = useSlot(slotId);
 */
export const useSlot = (id, options = {}) => {
  return useQuery({
    queryKey: slotKeys.single(id),
    queryFn: () => slotService.getSlot(id),
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 10,
    enabled: !!id, // Only run if id exists
    ...options
  });
};

// New hook for getting specific slot details for logged-in user
export const useSpecificSlot = () => {
  const { userId, isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: slotKeys.specific(userId),
    queryFn: () => slotService.getSpecificSlot(),
    enabled: isAuthenticated && !!userId,
    retry: 1,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes - only refetch after 5 minutes
    refetchInterval: (data) => {
      // Only auto-refetch every 5 minutes if no meeting link exists yet
      // This stops unnecessary calls once the link is available
      if (data?.slots?.[0]?.meeting_link) {
        return false; // Stop auto-refetch if meeting link exists
      }
      return 5 * 60 * 1000; // 5 minutes interval if no link
    },
    refetchIntervalInBackground: false, // Don't refetch when tab is not active
    // cacheTime: 0, // Don't cache the data to prevent stale data issues
  });
}; 